import React, {useCallback} from 'react'
import {Controller, useFormContext} from 'react-hook-form'
import {
  Box,
  Card,
  CardContent,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Button,
  Grid,
  styled,
  alpha,
} from '@mui/material'
import {CheckCircle, ArrowForward} from '@mui/icons-material'

import {useNavigate} from 'react-router-dom'
import {businessTypes} from './kycConfig'

const ContentCard = styled(Card)(({theme}) => ({
  maxWidth: 800,
  width: '100%',
  borderRadius: theme.spacing(3),
  overflow: 'hidden',
  background: 'rgba(255,255,255,0.95)',
  backdropFilter: 'blur(20px)',
}))

const HeaderSection = styled(Box)(({theme}) => ({
  background: 'linear-gradient(135deg, #3783e7 0%, #667eea 100%)',
  color: 'white',
  padding: theme.spacing(2, 4),
}))

const OptionsContainer = styled(Box)(({theme}) => ({
  padding: theme.spacing(4),
}))

const BusinessTypeCard = styled(Card)(({theme, selected}) => ({
  border: `2px solid ${selected ? '#3783e7' : 'transparent'}`,
  borderRadius: theme.spacing(2),
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: selected ? alpha('#3783e7', 0.05) : 'transparent',
  '&:hover': {
    border: `2px solid ${alpha('#3783e7', 0.5)}`,
  },
}))

const BusinessTypeContent = styled(CardContent)(({theme}) => ({
  padding: theme.spacing(2, 3),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
}))

const IconContainer = styled(Box)(({theme, selected}) => ({
  width: 40,
  height: 40,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: selected
    ? 'linear-gradient(135deg, #3783e7 0%, #667eea 100%)'
    : alpha('#3783e7', 0.1),
  color: selected ? 'white' : '#3783e7',
  transition: 'all 0.3s ease',
}))

const BusinessInfo = styled(Box)({
  flex: 1,
})

const BusinessTitle = styled(Typography)(({theme}) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
}))

const BusinessDescription = styled(Typography)(({theme}) => ({
  color: theme.palette.text.secondary,
}))

const RadioContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
})

const ContinueButton = styled(Button)(({theme}) => ({
  background: 'linear-gradient(135deg, #3783e7 0%, #667eea 100%)',
  color: 'white',
  padding: `${theme.spacing(1.5)} ${theme.spacing(4)}`,
  borderRadius: theme.spacing(3),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '1.1rem',

  '&:hover': {
    background: 'linear-gradient(135deg, #2c5aa0 0%, #5a67d8 100%)',
  },
  '&:disabled': {
    background: alpha('#3783e7', 0.3),
    transform: 'none',
    boxShadow: 'none',
  },
}))

export default function BusinessType() {
  const {control, watch} = useFormContext()
  const watchedBusinessType = watch('businessType')

  const navigation = useNavigate()

  const onContinueHandler = useCallback(async () => {
    navigation('/profile/verfication/document-upload')
  }, [navigation])

  return (
    <>
      <ContentCard>
        <HeaderSection>
          <Typography variant='body1' fontWeight={500} gutterBottom>
            Please confirm Type of your Business Entity
          </Typography>
          <Typography variant='body2'>
            Please select your business type to proceed with KYC registration and verification. This
            will help us tailor the process to your specific business requirements and ensure a
            smooth onboarding experience.
          </Typography>
        </HeaderSection>

        <OptionsContainer>
          <Controller
            name='businessType'
            control={control}
            render={({field}) => (
              <FormControl component='fieldset' fullWidth>
                <RadioGroup
                  {...field}
                  value={field.value}
                  onChange={(e) => {
                    field.onChange(e.target.value)
                  }}
                >
                  <Grid container spacing={3}>
                    {businessTypes.map((type) => {
                      const IconComponent = type.icon
                      const isSelected = field.value === type.title

                      return (
                        <Grid item xs={12} key={type.title}>
                          <BusinessTypeCard
                            selected={isSelected}
                            onClick={() => {
                              field.onChange(type.title)
                            }}
                          >
                            <BusinessTypeContent>
                              <IconContainer selected={isSelected}>
                                <IconComponent sx={{fontSize: 28}} />
                              </IconContainer>

                              <BusinessInfo>
                                <BusinessTitle variant='body1' fontWeight={500}>
                                  {type.title}
                                </BusinessTitle>
                                <BusinessDescription variant='body2'>
                                  {type.description}
                                </BusinessDescription>
                              </BusinessInfo>

                              <RadioContainer>
                                <FormControlLabel
                                  value={type.title}
                                  control={
                                    <Radio
                                      sx={{
                                        color: '#3783e7',
                                        '&.Mui-checked': {
                                          color: '#3783e7',
                                        },
                                      }}
                                    />
                                  }
                                  label=''
                                  sx={{margin: 0}}
                                />
                                {isSelected && (
                                  <CheckCircle
                                    sx={{
                                      color: '#3783e7',
                                      ml: 1,
                                      fontSize: 20,
                                    }}
                                  />
                                )}
                              </RadioContainer>
                            </BusinessTypeContent>
                          </BusinessTypeCard>
                        </Grid>
                      )
                    })}
                  </Grid>
                </RadioGroup>
              </FormControl>
            )}
          />

          <Box sx={{mt: 4}}>
            <ContinueButton
              type='button'
              onClick={onContinueHandler}
              size='large'
              disabled={!watchedBusinessType}
              endIcon={<ArrowForward />}
            >
              Continue with{' '}
              {businessTypes.find((type) => type.title === watchedBusinessType)?.title ||
                'Selection'}
            </ContinueButton>
          </Box>
        </OptionsContainer>
      </ContentCard>
    </>
  )
}
